import type { Department } from '../config/businesses';

export interface DepartmentNode {
  department: Department;
  children: Department[];
}

const byName = (a: Department, b: Department) =>
  a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });

const isChild = (d: Department) => d.parentId != null || !!d.parentName;

/**
 * Arma el árbol de dos niveles (departamento principal → subdepartamentos)
 * desde la lista plana que manda el back. Cuelga cada hijo por `parentId` y,
 * si el back no lo manda, por `parentName`. Un hijo cuyo padre no aparece se
 * muestra como principal para no dejar a nadie sin opción.
 */
export const buildDepartmentTree = (
  departments: Department[]
): DepartmentNode[] => {
  const roots = departments.filter(d => !isChild(d));
  const nodes = new Map<Department, Department[]>(roots.map(r => [r, []]));

  const findParent = (child: Department) =>
    roots.find(r =>
      child.parentId != null && r.id != null
        ? r.id === child.parentId
        : r.name === child.parentName
    );

  departments.filter(isChild).forEach(child => {
    const parent = findParent(child);
    if (parent) {
      nodes.get(parent)!.push(child);
    } else {
      nodes.set(child, []);
    }
  });

  return Array.from(nodes.entries())
    .map(([department, children]) => ({
      department,
      children: [...children].sort(byName),
    }))
    .sort((a, b) => byName(a.department, b.department));
};
